/**
 * Upload Progress UI Module — single source of truth for window.initUploadProgress.
 *
 * History (read tool_speed.md / tool_voice_changer.md / tool_trim.md for full
 * context, especially Step 0 of reference_etalon_fix_workflow.md):
 *
 *   1. Originally lived inside timbrica-core.js. timbrica-core was loaded
 *      with `<script src=cdn onerror=fallback>`; when the primary CDN
 *      failed, the parser advanced past the script element while the
 *      fallback fetch was still in flight, so the next inline tool-script
 *      ran with `initUploadProgress` undefined → ReferenceError on /ru/speed
 *      (Sentry, 2026-05-02). Commit `bbc283272` moved the function inline
 *      into layouts/app.blade.php on the theory that "inline definitions
 *      cannot lose a load race."
 *
 *   2. Sentry showed 4 users across /ru/voice-changer, /ru/speed, /ru/trim
 *      hitting the same ReferenceError in the 14 days *after* the inline
 *      move (commit `4124701c8`). A single inline `<script>` can still be
 *      suppressed in the long tail — Chromium content-blocking extensions
 *      that strip unrecognised inline scripts, malformed upstream values
 *      that throw before assignment, etc. Triage: a no-op stub at the top
 *      of timbrica-core.js (commit `4124701c8`) so the page didn't hard-
 *      error, but lost the progress UI for affected users.
 *
 *   3. THIS FILE (real fix): the implementation lives in a dedicated JS file
 *      loaded via a synchronous `<script @cspNonce src=…>` tag *before* the
 *      inline tool-scripts that depend on it. Same-origin for RU users (CSP
 *      `'self'` always allows it), CDN with onerror=fallback for non-RU.
 *      The stub in timbrica-core.js stays as a third line of defense —
 *      different suppression vector than this file (timbrica-core is loaded
 *      from a separate `<script src>` with its own onerror chain), so even
 *      if THIS file fails to load AND we redeploy with a typo'd path, the
 *      stub still keeps callers from hard-erroring.
 *
 * Public API (unchanged across all 20+ call sites — zero migration cost):
 *   const progress = initUploadProgress(overlayId, contentId?, fileInputId?);
 *   progress.show('processing…', 50);
 *   progress.hide();
 *   await progress.readFile(file);              // → ArrayBuffer (with progress UI)
 *   progress.cancelled;                          // boolean (set by Cancel button)
 *   progress.reset();                            // clear cancelled flag
 *   progress.onCancel = () => { ... };
 *
 * If `contentId` / `fileInputId` are omitted, we self-discover them inside
 * the nearest `.tool-uploader` ancestor (handy for tools using the shared
 * `<x-tool-uploader>` component — they don't have to know the internal IDs).
 */
(function(){
'use strict';

window.initUploadProgress = function(overlayId, contentId, fileInputId) {
    var overlay = document.getElementById(overlayId);
    // Defensive: an overlay-missing zone returns a no-op controller so the
    // tool keeps working (no progress UI, but no crash either).
    if (!overlay) {
        return {
            show: function(){},
            hide: function(){},
            readFile: function(){ return Promise.reject(new Error('upload progress overlay missing: ' + overlayId)); },
            cancelled: false,
            reset: function(){},
            set onCancel(fn){}
        };
    }

    // Self-discover content/input for tools on <x-tool-uploader>: find the
    // sibling .tu-content inside the same .tool-uploader zone, and the
    // hidden <input type=file> that tool-uploader.js mounted on body for it.
    var content = contentId ? document.getElementById(contentId) : null;
    if (!content) {
        var zone = overlay.closest('.tool-uploader');
        if (zone) content = zone.querySelector('.tu-content');
    }
    var fileInput = fileInputId ? document.getElementById(fileInputId) : null;
    if (!fileInput) {
        var z2 = overlay.closest('.tool-uploader');
        if (z2 && z2.__uploaderInput) fileInput = z2.__uploaderInput;
    }

    var textEl    = overlay.querySelector('[data-progress-text]');
    var barEl     = overlay.querySelector('[data-progress-bar]');
    var pctEl     = overlay.querySelector('[data-progress-pct]');
    var cancelBtn = overlay.querySelector('[data-progress-cancel]');
    var cancelled = false;
    var _onCancel = null;

    function show(text, pct) {
        if (content) content.style.display = 'none';
        overlay.classList.add('active');
        if (fileInput) fileInput.style.display = 'none';
        if (text && textEl) textEl.textContent = text;
        if (pct !== undefined && barEl && pctEl) {
            barEl.style.width = Math.round(pct) + '%';
            pctEl.textContent = Math.round(pct) + '%';
        }
    }
    function hide() {
        if (content) content.style.display = '';
        overlay.classList.remove('active');
        if (fileInput) fileInput.style.display = '';
        if (barEl) barEl.style.width = '0';
        if (pctEl) pctEl.textContent = '0%';
    }
    if (cancelBtn) {
        cancelBtn.onclick = function() {
            cancelled = true;
            hide();
            if (_onCancel) _onCancel();
        };
    }
    function readFile(file) {
        show(file.name, 5);
        // readFileBuffer comes from timbrica-core.js — if that script failed
        // to load, fall back to FileReader directly. No progress on the bar
        // during the read, but the tool still processes the file rather than
        // hanging on a helper that never arrives.
        if (typeof window.readFileBuffer === 'function') {
            return window.readFileBuffer(file, function(loaded, total) {
                if (total > 0) show(file.name, 5 + (loaded / total) * 85);
            });
        }
        return new Promise(function(resolve, reject) {
            var fr = new FileReader();
            fr.onload  = function() { show(file.name, 90); resolve(fr.result); };
            fr.onerror = function() { reject(fr.error || new Error('FileReader failed')); };
            fr.readAsArrayBuffer(file);
        });
    }
    return {
        show: show,
        hide: hide,
        readFile: readFile,
        get cancelled() { return cancelled; },
        set cancelled(v) { cancelled = v; },
        reset: function() { cancelled = false; },
        set onCancel(fn) { _onCancel = fn; }
    };
};

})();
